// Normalizers
// A function to convert whatever value the user has entered into the value that you want stored in the Redux store for the field.
// Docs: https://redux-form.com/7.3.0/docs/api/field.md/#-code-format-value-name-gt-formattedvalue-code-optional-
import {getAppLanguage} from 'client/common/config';

export const normalizeStringToBoolean = (value) => value === 'true';

export const normalizeStringToUpperCase = (value) => value.toUpperCase();

export const normalizeToSelectOption = (v) => {
  return v.value ? String(v.value) : '';
};

export const normalizeToNull = (value) => (value === 'null' ? null : value);

export const normalizeVariablesToIds = (variables) => (value) => {
  const regex = /{{([^}{]*)}}/g;
  let result = value;
  let match = regex.exec(value);

  while (match) {
    const variable = variables.find((i) => i.full_name === match[1]); // eslint-disable-line no-loop-func
    result = result.replace(match[0], `{{${variable ? variable.id : match[1]}}}`);
    match = regex.exec(value);
  }

  return result;
};

// Formatters
// Formats the value from the Redux store to be displayed in the field input
// Docs: https://redux-form.com/7.3.0/docs/api/field.md/#-code-normalize-value-previousvalue-allvalues-previousallvalues-gt-nextvalue-code-optional-

export const formatToString = (value) => String(value);

export const formatToSelectOption = (v) => {
  return v ? {value: String(v)} : {};
};

export const formatIdsToVariables = (variables) => (value) => {
  const regex = /{{([0-9]*)}}/g;
  let result = value;
  let match = regex.exec(value);

  while (match) {
    const variable = variables.find((i) => String(i.id) === match[1]); // eslint-disable-line no-loop-func
    result = result.replace(match[0], `{{${variable ? variable.full_name : match[1]}}}`);
    match = regex.exec(value);
  }

  return result;
};

export const formatNumber = (number) => (number.toString().length === 1 ? `0${number}` : number);

export function formatNumberSpace(number) {
  return number?.toString()
    ? new Intl.NumberFormat('en-Latn-US', {style: 'decimal'}).format(number).replace(/,/g, ' ')
    : '';
}

export function formatFloat(number) {
  return number.toLocaleString(getAppLanguage());
}

export function formatFileSize(size) {
  if (!size) {
    return null;
  }

  const units = ['b', 'kb', 'mb', 'gb', 'tb'];
  const index = Math.floor(Math.log(size) / Math.log(1024));
  const unit = units[index];
  const value = (size / Math.pow(1024, index)).toFixed(2) * 1;

  return `${value} ${unit}`;
}

export const capitalizeFirstLetter = (string) => (string ? string[0].toUpperCase() + string.slice(1) : '');

export const removeSpaces = (value) => value?.replaceAll(' ', '') || '';

/**
 * Transforms an array of option objects into a format suitable for select inputs.
 *
 * @param {Array} options - Array of option objects, each containing a `name` and `id`.
 * @returns {Array} Transformed array where each object has a `label` and `value`.
 */
export const transformObjToOptions = (options, settings) => {
  const {labelKey = 'name', valueKey = 'id'} = settings || {};

  return options.map((option) => {
    return {
      label: option[labelKey],
      value: option[valueKey],
    };
  });
};

/**
 * Leaves only positive numbers in the string
 *
 * @param {string|null} value
 * @returns {string|null}
 */
export const onlyPositiveNumbers = (value) => {
  if (value && typeof value === 'string') {
    return value.replace(/\D/g, '').replace(/^0+/, '');
  }
  return value;
};

/**
 * Leaves only positive numbers and zero in the string
 *
 * @param {string|null} value
 * @returns {string|null}
 */
export const onlyPositiveNumbersAndZero = (value) => {
  if (value && typeof value === 'string') {
    return value === '0' ? value : onlyPositiveNumbers(value);
  }
  return value;
};

/**
 * Leaves only letters and numbers in the string
 *
 * @param {string|null} value
 * @returns {string|null}
 */
export const onlyLettersAndNumbers = (value) => {
  if (value && typeof value === 'string') {
    return value.replace(/[^a-zA-Z0-9_]/g, '');
  }
  return value;
};
